import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "./components/calc-slider";
import "./components/car-selector";
import "./components/cost-summery";
import "./components/cost-summary-sticky";
import "./components/radio-group";
import type { RadioOption } from "./components/radio-group";
import type { DetailItem } from "./components/cost-summery";
import carsData from "./cars.json";

interface CarVariant {
  name: string;
  image: string;
  price: number;
  benefitValue: number;
  insurance: number;
  service: number;
  tax: number;
  wltp: number;
}

interface CarModel {
  model: string;
  variants: CarVariant[];
}

interface GlobalData {
  interest: number;
  residualValue: number;
  socialTax: number;
  governmentLoanInterestRate: number;
  priceBaseAmount: number;
  service: number;
}

interface PriceCalculatorData {
  cars: CarModel[];
  globalData: GlobalData;
}

@customElement("price-calculator")
export class PriceCalculator extends LitElement {
  @property({ type: String }) cars = "";

  @state() selectedCar = "Forester";
  @state() price = 0;
  @state() priceNoVat = 0;
  @state() selectedCarImage ="https://images.unsplash.com/photo-1619767886558-efdc259cde1a?w=800&h=600&fit=crop";
  @state() totalDistance = 1500;
  @state() businessDistance = 500;
  @state() downPayment = 20;
  @state() downPaymentTotal = 0;
  @state() marginalTax = 50;

  @state() contractPeriod = "36";
  @state() hasFuelBenefit = "2";
  @state() electricityPrice = 3;
  @state() driverFuelCostNet = 0;
  @state() driverFuelCostGross = 0;
  @state() companyFuelCost = 200;
  @state() WLTP = 16.0;
  @state() benefitValueGross = 0;
  @state() benefitValueNet = 0;
  @state() insurance = 0;
  @state() service = 0;
  @state() tax = 0;

  @state() additionalMileageCost = 690;
  @state() additionalMileageMultiplier = 0;

  // Global data
  @state() globalInterest = 0;
  @state() globalResidualValue = 0;
  @state() globalSocialTax = 0;
  @state() globalGovernmentLoanInterestRate = 0;
  @state() globalPriceBaseAmount = 0;
  @state() globalService = 0;

  @state() driverTotalCost = 0;
  @state() financialLeasing = 0;
  @state() nonDeductableVatLeasing = 0;
  @state() serviceTotalCost = 0;
  @state() companySocialTaxCar = 0;
  @state() companySocialTaxFuel = 0;
  @state() companyTotalCost = 0;

  private carsData: CarModel[] = carsData as CarModel[];

  connectedCallback() {
    super.connectedCallback();

    // Parse cars data from attribute if provided
    if (this.cars) {
      try {
        const parsedData: PriceCalculatorData = JSON.parse(this.cars);

        if (Array.isArray(parsedData.cars) && parsedData.cars.length > 0) {
          this.carsData = parsedData.cars;

          // Set initial selected car from first variant
          if (this.carsData[0]?.variants?.length > 0) {
            const firstVariant = this.carsData[0].variants[0];
            this.selectedCar = firstVariant.name;
            this.applyVariant(firstVariant);
          }
        }

        // Set global data if provided
        if (parsedData.globalData) {
          this.globalInterest = parsedData.globalData.interest || this.globalInterest;
          this.globalResidualValue = parsedData.globalData.residualValue || this.globalResidualValue;
          this.globalSocialTax = parsedData.globalData.socialTax || this.globalSocialTax;
          this.globalGovernmentLoanInterestRate = parsedData.globalData.governmentLoanInterestRate || this.globalGovernmentLoanInterestRate;
          this.globalPriceBaseAmount = parsedData.globalData.priceBaseAmount || this.globalPriceBaseAmount;
          this.globalService = parsedData.globalData.service || this.globalService;
        }
      } catch (error) {
        console.error("Failed to parse cars data:", error);
      }
    }

    this.updateAllCalculations();
  }

  createRenderRoot() {
    return this;
  }

  private getSelectedVariant() {
    for (const car of this.carsData) {
      const variant = car.variants.find((v) => v.name === this.selectedCar);
      if (variant) {
        return variant;
      }
    }
    return null;
  }

  private applyVariant(variant: CarVariant) {
    this.selectedCarImage = variant.image;
    this.price = variant.price ?? this.price;
    this.priceNoVat = this.price * 0.8;
    this.WLTP = variant.wltp ?? this.WLTP;
    this.benefitValueGross = variant.benefitValue ?? this.benefitValueGross;
    this.insurance = variant.insurance ?? this.insurance;
    this.service = variant.service ?? this.service;
    this.tax = variant.tax ?? this.tax;
  }

    private updateBenefitValueNet() {
    // Excel formula: K11*G25
    // K11 = benefitValueGross, G25 = 1 - marginalTax / 100
    //console.log((1 - this.marginalTax / 100));
        //this.benefitValueNet = this.floorToInt(this.benefitValueGross * (1 - this.marginalTax / 100));
        this.benefitValueNet = this.benefitValueGross * (this.marginalTax / 100);
  }

  private updateDriverFuelCost() {
    // Excel formula: =OM(G18=2;0;(((((F9)-(F12))/12)*(G15*(F25/10))*1,2)*0,5))
    //=OM(G18=2;0;(((((F9)-(F12))/12)*(G15*(F28/10))*1,2))) - Brutto
    // G18 = hasFuelBenefit, F9 = totalDistance, F12 = businessDistance
    // G15 = electricityPrice, F25 = WLTP
    if (parseInt(this.hasFuelBenefit) === 0) {
      this.driverFuelCostGross = 0;
    } else {
      this.driverFuelCostGross = ((this.totalDistance - this.businessDistance) / 12) * (this.electricityPrice * (this.WLTP / 10)) * 1.2;
    }
    
    if (parseInt(this.hasFuelBenefit) === 0) {
      this.driverFuelCostNet = 0;
    } else {
      this.driverFuelCostNet = this.driverFuelCostGross * (this.marginalTax / 100);
    }

  }

  private updateDriverTotalCost() {
    //Total cost for driver in fuel netto + benefit value netto
      this.driverTotalCost = this.benefitValueNet + this.driverFuelCostNet;
  }
  
  private updateAdditionalMileageMultiplier() {
    //Mutliplier for additional mileage cost, increases by 0.5 for every 500 miles over 1500
    //=(OM((F9*100)>1500;(F9*100)-1500;0))/500
    if (this.totalDistance > 1500) {
        //const extraIntervals = Math.floor((this.totalDistance - 1500) / 500);
        const extraIntervals = (this.totalDistance - 1500) / 500;
      this.additionalMileageMultiplier = extraIntervals;
    } else {
      this.additionalMileageMultiplier = 0;
    }
    //this.driverTotalCost = this.ceilToInt(this.benefitValueNet + this.driverFuelCostNet);
  }

  private updateFinancialLeasing() {
    // New Excel formula =(BETALNING(F2/12;36;-K20+K21;K20*F3;1))+(K23*K24)
    // F2 = globalInterest
    // 36 = contract period in months
    // K20 = this.priceNoVat
    // K21 = this.downPaymentTotal
    // F3 = this.globalResidualValue
    // K23 = this.additionalMileageCost
    // K24 = this.additionalMileageMultiplier
    this.downPaymentTotal = this.priceNoVat * (this.downPayment / 100);
    const contractPeriod = Number.parseInt(this.contractPeriod, 10) || 36;

      this.financialLeasing = this.ceilToInt(
       this.PMT(
         this.globalInterest / 12, contractPeriod, -this.priceNoVat + this.downPaymentTotal, this.priceNoVat * this.globalResidualValue, 1) +
         this.additionalMileageCost * this.additionalMileageMultiplier
     );
    
    // test formula with fixed values
    // this.financialLeasing = this.ceilToInt(this.PMT(0.05 / 12, 36, -(427920 + 0), 427920 * 0.55, 1) + 690 * 1);
    // detta retunerar 7414 vilket inte stämmer överens med excel som retunerar 7483
    // console.log("Financial Leasing:", this.financialLeasing);

    this.updateNonDeductableVatLeasing();
  }

  private updateNonDeductableVatLeasing() {
    // Excel: =K20*0,125
    // K20 = Finacial leasing
      //this.nonDeductableVatLeasing = this.floorToInt(this.financialLeasing * 0.125);
      this.nonDeductableVatLeasing = this.financialLeasing * 0.125;
  }

  private updateServiceTotalCost() {
    // Excel formula: =(OM(((F9*3)<=4500;0;(((F9*3)-4500)*F29))/36
    const contractPeriod = Number.parseInt(this.contractPeriod, 10) || 36;
    const totalDistanceForPeriod = this.totalDistance * 3;

    if (totalDistanceForPeriod <= 4500) {
      this.serviceTotalCost = 0;
    } else{
        //this.serviceTotalCost = this.ceilToInt(((totalDistanceForPeriod - 4500) * this.globalService) / contractPeriod);
        this.serviceTotalCost = ((totalDistanceForPeriod - 4500) * this.globalService) / contractPeriod;
    }
  
  }

  private updateCompanyFuelCost() {
    // Excel formula: =OM(G18=2;0;((F9)/12/1)*((G15*0,8)*(F25/10)))
    // Excel formula: =(OM(G18=2;(F12/12)*9,5;(H9/1)*((G15*0,8)*(F28/10))))
    // G18 = hasFuelBenefit
    // F12 = businessDistance
    // G15 = electricityPrice
    // F28 = WLTP

    //=( OM( G18=2;( (F12*100)/12)*9,5;(H9/1)*((G15*0,8)*(F28/10) ) ) )

    //if G18 true (F12*100)/12)*9,5
    if (parseInt(this.hasFuelBenefit) === 0) {
        //this.companyFuelCost = this.ceilToInt((this.businessDistance / 12) * 9.5);
        this.companyFuelCost = (this.businessDistance / 12) * 9.5;
    } else {
        //this.companyFuelCost = this.floorToInt((this.totalDistance / 12) * (this.electricityPrice * 0.8 * (this.WLTP / 10)));
        this.companyFuelCost = (this.totalDistance / 12) * (this.electricityPrice * 0.8 * (this.WLTP / 10));
    }
  }

  private updateCompanySocialTaxCar() {
    // Excel formula: =(K11*2)*F4
    // K11 = benefitValueGross, F4 = globalSocialTax
      //this.companySocialTaxCar = this.ceilToInt(this.benefitValueGross * 2 * this.globalSocialTax);
      this.companySocialTaxCar = this.benefitValueGross * 1 * this.globalSocialTax;
  }

  private updateCompanySocialTaxFuel() {
    // Excel formula: =(K12*2)*F4
    // K12 = driverFuelCostNet, F4 = globalSocialTax
      //this.companySocialTaxFuel = this.floorToInt((this.driverFuelCostGross * 2) * this.globalSocialTax);
      this.companySocialTaxFuel = (this.driverFuelCostGross * 1) * this.globalSocialTax;
  }

  private updateCompanyTotalCost() {
    this.companyTotalCost = this.floorToInt(
        this.financialLeasing 
      + this.nonDeductableVatLeasing
      + this.additionalMileageCost 
      + this.additionalMileageMultiplier
      + this.serviceTotalCost
      + this.tax
      + this.companyFuelCost
      + this.insurance
      + this.companySocialTaxCar
      + this.companySocialTaxFuel);
  }

  private updateAllCalculations() {
    this.updateBenefitValueNet();
    this.updateAdditionalMileageMultiplier();
    this.updateFinancialLeasing();
    this.updateServiceTotalCost();
    this.updateCompanyFuelCost();
    this.updateDriverFuelCost();
    this.updateDriverTotalCost();
    this.updateCompanySocialTaxCar();
    this.updateCompanySocialTaxFuel();
    this.updateCompanyTotalCost();
  }

  private ceilToInt(value: number): number {
    return Math.ceil(value);
  }
  
  private floorToInt(value: number): number {
    return Math.floor(value);
  }

  private PMT(
    rate: number,
    nper: number,
    pv: number,
    fv: number = 0,
    type: 0 | 1 = 0
  ): number {
    if (!Number.isFinite(rate) || !Number.isFinite(nper) || !Number.isFinite(pv) || !Number.isFinite(fv)) {
      throw new Error("PMT: inputs must be finite numbers");
    }
    if (!Number.isFinite(nper) || nper === 0) {
      throw new Error("PMT: nper must be a non-zero number");
    }

    if (rate === 0) {
      return -(pv + fv) / nper;
    }

    const pow = Math.pow(1 + rate, nper);

    let pmt = (rate * (fv + pv * pow)) / (pow - 1);

    if (type === 1) {
      pmt /= (1 + rate);
    }

    return -pmt;
  }

  private handleCarChange(e: CustomEvent) {
    this.selectedCar = e.detail;
    const variant = this.getSelectedVariant();
    if (variant) {
      this.applyVariant(variant);
    }
    this.updateAllCalculations();
  }

  private contractOptions: RadioOption[] = [
    { value: "24", label: "24 mån" },
    { value: "36", label: "36 mån" },
  ];

  private fuelBenefitOptions: RadioOption[] = [
    { value: "2", label: "Ja" },
    { value: "0", label: "Nej" },
  ];

  render() {
    return html`
      <div
        class="grid grid-cols-12 gap-6 mx-auto max-w-[1120px] mb-10 site-margins"
      >
        <!-- LEFT PANEL - Image & Car Selector -->
        <div class="col-span-12 md:col-span-6 flex">
          <img
            src="${this.selectedCarImage}"
            alt="Car in nature"
            class="w-full object-cover h-[595px] flex-1 aspect-[4/5]"
          />
        </div>

        <!-- RIGHT PANEL - Calculator -->
        <div class="col-span-12 md:col-span-6">
          <car-selector
            selectId="car-selector"
            .selected=${this.selectedCar}
            .cars=${this.carsData}
            @value-change=${this.handleCarChange}
          ></car-selector>

          <calc-slider
            labelId="total-distance-slider"
            label="Total körsträcka/år"
            .value=${this.totalDistance}
            unit="mil"
            .min=${0}
            .max=${4500}
            .step=${500}
            @value-change=${(e: CustomEvent) => {
              this.totalDistance = e.detail;
              this.updateAllCalculations();
            }}
          ></calc-slider>

          <calc-slider
            labelId="business-distance-slider"
            label="Körsträcka i tjänst/år"
            .value=${this.businessDistance}
            unit="mil"
            .min=${0}
            .max=${4500}
            .step=${100}
            @value-change=${(e: CustomEvent) => {
              this.businessDistance = e.detail;
              this.updateAllCalculations();
            }}
          ></calc-slider>

          <calc-slider
            labelId="down-payment-slider"
            label="Kontantinsats"
            .value=${this.downPayment}
            unit="%"
            .min=${0}
            .max=${30}
            .step=${5}
            @value-change=${(e: CustomEvent) => {
              this.downPayment = e.detail;
              this.updateAllCalculations();
            }}
          ></calc-slider>

          <calc-slider
            labelId="marginal-tax-slider"
            label="Marginalskatt"
            .value=${this.marginalTax}
            unit="%"
            .min=${30}
            .max=${58}
            .step=${1}
            @value-change=${(e: CustomEvent) => {
              this.marginalTax = e.detail;
              this.updateAllCalculations();
            }}
          ></calc-slider>

          <radio-group
            labelId="contract-period"
            label="Avtalstid"
            name="contract-period"
            .options=${this.contractOptions}
            .selected=${this.contractPeriod}
            @value-change=${(e: CustomEvent) => {
              this.contractPeriod = e.detail;
              this.updateAllCalculations();
            }}
          ></radio-group>

          <radio-group
            labelId="fuel-benefit"
            label="Har du drivmedelsförmån?"
            name="fuel-benefit"
            .options=${this.fuelBenefitOptions}
            .selected=${this.hasFuelBenefit}
            @value-change=${(e: CustomEvent) => {
              this.hasFuelBenefit = e.detail;
              this.updateAllCalculations();
            }}
          ></radio-group>

          <calc-slider
            labelId="electricity-price-slider"
            label="Elpris"
            .value=${this.electricityPrice}
            unit="kr/kWh"
            .min=${0}
            .max=${15}
            .step=${1}
            @value-change=${(e: CustomEvent) => {
              this.electricityPrice = e.detail;
              this.updateAllCalculations();
            }}
          ></calc-slider>
        </div>
      </div>

      <!-- SUMMARY -->
      <div id="details" class="bg-neutral scroll-mt-8">
        <div
          class="grid grid-cols-12 gap-6 py-16 max-w-[1120px] mx-auto site-margins"
        >
          <div class="col-span-12">
            <h2 class="text-3xl font-bold mb-6">Din totalkostnad/månad</h2>
          </div>

          <!-- LEFT PANEL -->
          <div class="col-span-12 md:col-span-6">
            <cost-summery
              title="Förarens kostnad efter skatteavdrag"
              .totalCost=${Math.floor(this.driverTotalCost)}
              subtitle="Förmånsvärde + drivmedel"
              .detailItems=${[
        { title: "Förmånsvärde brutto", value: Math.floor(this.benefitValueGross) },
        { title: "Förmånsvärde netto", value: Math.floor(this.benefitValueNet) },

                { title: "Drivmedelskostnad Brutto", 
                  value: Math.floor(this.driverFuelCostGross), 
                  visible: this.hasFuelBenefit === "2" ? true : false 
                },
                { title: "Drivmedelskostnad Netto", 
                  value: Math.floor(this.driverFuelCostNet), 
                  visible: this.hasFuelBenefit === "2" ? true : false 
                },
                
              ] as DetailItem[]}
              info=""
            ></cost-summery>
          </div>

          <!-- RIGHT PANEL -->
          <div class="col-span-12 md:col-span-6">
            <cost-summery
              title="Företagets leasing"
              .totalCost=${Math.floor(this.financialLeasing)}
              subtitle="Exkl. moms"
              .detailItems=${[
                { title: "Bilens pris exkl. moms", value: this.priceNoVat},
                { title: "Kontantinsats", value: this.downPaymentTotal},
                { title: "Finansiell leasing",value: Math.floor(this.financialLeasing)},
                /*{ title: "Tillägg på ökad körsträcka", value: Math.ceil(this.additionalMileageCost)},
                { title: "Tillägg på ökad körsträcka Multiplier", value: Math.ceil(this.additionalMileageMultiplier)},*/
                { title: "Ej avdragbar moms (leasing)", value: Math.ceil(this.nonDeductableVatLeasing)},
                { title: "Servicekostnad", value: Math.ceil(this.serviceTotalCost)},
                { title: "Fordonsskatt", value: Math.ceil(this.tax)},
                { title: "Laddkostnad", value: Math.floor(this.companyFuelCost), visible: this.hasFuelBenefit === "2" ? true : false},
                { title: "Milersättning", value: Math.ceil(this.companyFuelCost), visible: this.hasFuelBenefit === "0" ? true : false},
                { title: "Försäkring", value: Math.ceil(this.insurance)},
                { title: "Sociala avgifter bil", value: Math.ceil(this.companySocialTaxCar)},
                { title: "Sociala avgifter drivmedel", value: Math.floor(this.companySocialTaxFuel),visible: this.hasFuelBenefit === "2" ? true : false},
                { title: "Företagets kostnad", titleClass: "font-bold", value: this.companyTotalCost},
              ] as DetailItem[]}
              info=""
            ></cost-summery>
          </div>
        </div>
      </div>

      <!-- STICKY SUMMARY -->
      <cost-summary-sticky
        .driverCost=${Math.floor(this.driverTotalCost)}
        .companyCost=${Math.floor(this.financialLeasing)}
      ></cost-summary-sticky>
    `;
  }
}
